import React, { FC } from 'react'
import {
  Stack,
  Heading,
  Text,
  Box,
  Flex,
  IncrementInput,
  PseudoBox,
  Link,
  FlexProps,
  Price,
  Image,
  useToast,
} from '@thirstycamel/ui'

import imageProxy from '../../utils/imageProxy'
import { useActions, useStore } from '../../store/hooks'
import { getNiceErrorMessage } from '../../utils/getNiceErrorMessage'
import CartItemEntity from '@ts/core/src/modules/cart/cartitem.entity'
import { useLockedProductLists } from '../../hooks/useLockedProductLists'
import { getProductLinkProps } from '../../utils/productUrl'

export interface CartLineProductProps extends FlexProps {
  line: CartItemEntity
  bundleGroup?: string
  hideQuantity?: boolean
  hidePrice?: boolean
}

export const CartLineProduct: FC<CartLineProductProps> = ({
  line,
  bundleGroup,
  hideQuantity,
  hidePrice,
  ...restProps
}) => {
  const toast = useToast()
  const { getReturnUrlFromPricelist } = useLockedProductLists()

  const { changeCartItemQty, fetchCart } = useActions(s => s.cart)
  const isFetching = useStore(s => s.cart.isFetching)

  const price = line.specialPricing?.price ?? line.pricing?.price ?? 0

  const updateQuantity = quantity => {
    changeCartItemQty({
      qty: quantity,
      cartItem: line.id,
    })
      .then(fetchCart)
      .catch(error => {
        toast({
          title: getNiceErrorMessage(error),
          status: 'error',
        })
        fetchCart()
      })
  }

  const handleChangeQuantity = value => {
    if (value !== line.qty) updateQuantity(value)
  }

  const handleRemove = () => {
    updateQuantity(0)
  }

  const { href, hrefAs } = getProductLinkProps(line.product, {
    bundleGroup,
    pricelist: line?.priceList,
  })

  return (
    <Flex
      direction={['column', 'row', 'column', 'row']}
      justify="space-between"
      align={['flex-start', 'center', 'flex-start', 'center']}
      {...restProps}
    >
      <Stack spacing={5} isInline align="center" mb={[4, 0, 2, 0]}>
        <Stack spacing={5} isInline align="center">
          <Link href={href} hrefAs={hrefAs} flexShrink={0} isDisabled={isFetching}>
            <Image
              src={imageProxy(line.product?.media?.[0]?.filename, { height: 80 })}
              size="64px"
              objectFit="contain"
              border="1px"
              borderColor="gray.200"
              borderRadius="sm"
            />
          </Link>

          <Stack spacing={[0, , 1]} align="flex-start">
            <Link
              href={href}
              hrefAs={hrefAs}
              _hover={{ color: 'pink.500', textDecoration: 'underline' }}
              isDisabled={isFetching}
            >
              <Heading fontSize="sm" textTransform="uppercase">
                {line.product.productGroup.brand?.name} {line.product.productGroup.name}
              </Heading>
            </Link>

            <Flex align={[, , 'center']} direction={['column', , 'row']}>
              <Text fontSize="sm" color="gray.500" fontWeight="bold">
                {line.product.unitSize}mL &nbsp;&bull;&nbsp;&nbsp;
                {line.product.factorDescription}
                &nbsp; &bull;&nbsp;&nbsp;
              </Text>

              <PseudoBox
                as="button"
                color="pink.500"
                fontWeight="medium"
                fontSize="sm"
                display="inline-flex"
                outline={0}
                _focus={{ textDecoration: 'underline', boxShadow: 'outline' }}
                _hover={{ textDecoration: 'underline' }}
                onClick={!isFetching ? handleRemove : undefined}
                opacity={isFetching ? 0.6 : null}
                cursor={isFetching ? 'not-allowed' : null}
              >
                Remove
              </PseudoBox>
            </Flex>

            {line?.priceList && (
              <Stack isInline spacing={1} align="center">
                <Link
                  href={decodeURIComponent(getReturnUrlFromPricelist(line?.priceList))}
                  hrefAs={decodeURIComponent(getReturnUrlFromPricelist(line?.priceList))}
                >
                  <Text fontSize="xs" color="pink.500" fontWeight="bold">
                    {line?.priceList.replace(/-/g, ' ')} PRICE
                  </Text>
                </Link>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>

      <Stack isInline spacing={3} justify="flex-end" w={['100%', 'auto', '100%', 'auto']}>
        {!hideQuantity && (
          <IncrementInput
            value={line.qty ?? 1}
            size="sm"
            max={50}
            min={1}
            isDisabled={isFetching}
            onChangeValue={handleChangeQuantity}
          />
        )}

        {!hidePrice && <Price fontFamily="heading" fontSize="2xl" value={price * line.qty} />}
      </Stack>
    </Flex>
  )
}

export default CartLineProduct
